import { useEffect, useState } from "react";
import { Bookmark, Sparkles, User as UserIcon, Check, X, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { signInWithApple, signInWithGoogle } from "@/lib/auth";
import { isAndroid } from "@/lib/nativeAuth";
import { trackEvent, setSignupTrigger } from "@/lib/analytics";

function getGuestSessionId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem("guest_session_id");
  } catch {
    return null;
  }
}

const CORAL = "var(--l2f-coral)";
const CORAL_TINT = "var(--l2f-cream)";

export type SignupTrigger =
  | "save_outfit"
  | "lookbook_limit"
  | "saved_tab"
  | "account_tab"
  | "email_capture"
  | "customise_model"
  | "wardrobe_teaser"
  | "unsaved_preview"
  | "lb3_guest_cta"
  | "home_card_b";

type PendingAction =
  | { trigger: "save_outfit"; outfitId?: string | null }
  | { trigger: "lookbook_limit" }
  | { trigger: "saved_tab" }
  | { trigger: "account_tab" }
  | { trigger: "unsaved_preview"; outfitId: string }
  | { trigger: "lb3_guest_cta"; outfitId: string };

const PENDING_KEY = "pending_signup_action";

export function setPendingSignupAction(action: PendingAction) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(action));
  } catch {
    /* ignore */
  }
}
export function consumePendingSignupAction(): PendingAction | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    localStorage.removeItem(PENDING_KEY);
    return JSON.parse(raw) as PendingAction;
  } catch {
    return null;
  }
}

const COPY: Record<
  SignupTrigger,
  {
    icon: React.ReactNode;
    title: string;
    subtitle: string;
    benefits: string[];
    cta: string;
  }
> = {
  save_outfit: {
    icon: <Bookmark size={24} style={{ color: CORAL }} />,
    title: "Save this look",
    subtitle: "Create a free account to save this outfit and access it anytime.",
    benefits: [
      "Save unlimited outfits",
      "Generate AI lookbook models",
      "Shop at your budget anytime",
      "Style your own wardrobe items",
    ],
    cta: "Create account and save →",
  },
  lookbook_limit: {
    icon: <Sparkles size={24} style={{ color: CORAL }} />,
    title: "You've used your free looks for today",
    subtitle:
      "Sign up free for unlimited AI lookbook generations — no daily limits, ever.",
    benefits: [
      "Unlimited AI lookbook generations",
      "Save unlimited outfits",
      "Personalised model that looks like you",
      "Shop at your budget anytime",
    ],
    cta: "Sign up free — unlimited looks →",
  },
  saved_tab: {
    icon: <Bookmark size={24} style={{ color: CORAL }} />,
    title: "Your style library",
    subtitle:
      "Create a free account to save outfits and build your style library.",
    benefits: [
      "Save unlimited outfits",
      "Generate AI lookbook models",
      "Shop at your budget anytime",
      "Style your own wardrobe items",
    ],
    cta: "Create account and save →",
  },
  email_capture: {
    icon: <Mail size={24} style={{ color: CORAL }} />,
    title: "Save unlimited looks",
    subtitle:
      "Create a free account to save this look and find more outfits anytime.",
    benefits: [
      "Save unlimited outfits",
      "Access your looks anytime",
      "Generate AI lookbook models",
      "Style your own wardrobe items",
    ],
    cta: "Create free account →",
  },
  account_tab: {
    icon: <UserIcon size={24} style={{ color: CORAL }} />,
    title: "Your account",
    subtitle:
      "Create a free account to personalise your experience, save outfits and set your budget.",
    benefits: [
      "Set your personal budget",
      "Save your size preferences",
      "Build your lookbook model",
      "Save and revisit your outfits",
    ],
    cta: "Create free account →",
  },
  customise_model: {
    icon: <Sparkles size={24} style={{ color: CORAL }} />,
    title: "Want it to look exactly like you?",
    subtitle: "Add skin tone, hair and more — sign up to fine-tune your model.",
    benefits: [
      "Add skin tone, hair and features",
      "Save your personalised model",
      "Keep your looks across devices",
      "Generate unlimited lookbooks",
    ],
    cta: "Sign up free →",
  },
  wardrobe_teaser: {
    icon: <Bookmark size={24} style={{ color: CORAL }} />,
    title: "Style your own wardrobe",
    subtitle: "Create a free account to add your own items and style them.",
    benefits: [
      "Add and style your own wardrobe",
      "Save unlimited outfits",
      "Generate AI lookbook models",
      "Shop at your budget anytime",
    ],
    cta: "Create free account →",
  },
  unsaved_preview: {
    icon: <Bookmark size={24} style={{ color: CORAL }} />,
    title: "Save your look",
    subtitle:
      "Create an account to keep your look forever. Without saving, it expires in 7 days.",
    benefits: [
      "Keep your looks forever",
      "Generate AI lookbook models",
      "Shop at your budget anytime",
      "Style your own wardrobe items",
    ],
    cta: "Save my look →",
  },
  lb3_guest_cta: {
    icon: <Sparkles size={24} style={{ color: CORAL }} />,
    title: "See it with your wardrobe",
    subtitle:
      "Sign up to see this look styled with items you already own.",
    benefits: [
      "Style outfits with what you own",
      "Save unlimited outfits",
      "Generate AI lookbook models",
      "Shop at your budget anytime",
    ],
    cta: "Sign up free",
  },
  home_card_b: {
    icon: <Sparkles size={24} style={{ color: CORAL }} />,
    title: "See looks with your wardrobe",
    subtitle:
      "Sign up free to style every look with items you already own.",
    benefits: [
      "Style outfits with what you own",
      "Save unlimited outfits",
      "Generate AI lookbook models",
      "Shop at your budget anytime",
    ],
    cta: "Sign up free",
  },
};

export function InlineSignupSheet({
  isOpen,
  onClose,
  onSuccess,
  trigger,
  pendingAction,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  trigger: SignupTrigger;
  /** Persisted to localStorage so the action can complete after an OAuth redirect. */
  pendingAction?: PendingAction;
}) {
  const [mode, setMode] = useState<"signup" | "login">("signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setMode("signup");
      setEmail("");
      setPassword("");
      setError(null);
      setLoading(false);
    } else {
      const guestSessionId = getGuestSessionId();
      trackEvent("signup_prompt_shown", {
        trigger,
        is_guest: true,
        guest_session_id: guestSessionId,
      });
    }
  }, [isOpen, trigger]);

  if (!isOpen) return null;
  const copy = COPY[trigger];

  // Single chokepoint for every dismiss path (X tap, backdrop tap,
  // "Maybe later", success). Clears any pending_signup_action persisted
  // by persistForRedirect so a dismissed sheet (incl. cancelled ASWeb-
  // AuthenticationSession on iOS Path B2) never resumes a stale action
  // on the next sign-in.
  const handleClose = () => {
    if (typeof window !== "undefined") {
      try {
        localStorage.removeItem("pending_signup_action");
        localStorage.removeItem("pending_signup_target");
      } catch {
        /* ignore */
      }
    }
    onClose();
  };

  const persistForRedirect = () => {
    setSignupTrigger(trigger);
    if (pendingAction) setPendingSignupAction(pendingAction);
  };

  const handleGoogle = async () => {
    setError(null);
    persistForRedirect();
    const result = await signInWithGoogle();
    if (result.error) {
      setError(result.error.message ?? "Google sign-in failed");
      return;
    }
    if (!result.redirected) {
      trackEvent("signup_completed", { trigger });
      onSuccess();
      handleClose();
    }
  };

  const handleApple = async () => {
    setError(null);
    persistForRedirect();
    const result = await signInWithApple();
    if (result.error) {
      setError(result.error.message ?? "Apple sign-in failed");
      return;
    }
    if (!result.redirected) {
      trackEvent("signup_completed", { trigger });
      onSuccess();
      handleClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    persistForRedirect();
    try {
      if (mode === "signup") {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
        });
        if (error) throw error;
        if (!data.session) {
          throw new Error("Account created but session was not established. Please log in.");
        }
        trackEvent("signup_completed", { trigger });
        onSuccess();
        handleClose();
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        trackEvent("signup_completed", { trigger });
        onSuccess();
        handleClose();
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Something went wrong";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      onClick={handleClose}
    >
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative w-full max-w-md bg-background rounded-t-3xl px-6 pt-3 shadow-2xl max-h-[92vh] overflow-y-auto"
        style={{ paddingBottom: "calc(24px + var(--safe-bottom))" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto w-9 h-1 rounded-full bg-neutral-200 mb-4" />

        <>
          <div className="flex justify-center mb-3">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{ background: "var(--l2f-cream)" }}
              >
                {copy.icon}
              </div>
            </div>
            <p
              className="l2f-section-label text-center"
              style={{ color: "var(--l2f-coral)", marginBottom: 6 }}
            >
              {mode === "login" ? "WELCOME HOME" : "LIKE2FIT · THE INVITATION"}
            </p>
            <h3
              className="text-center"
              style={{
                fontFamily: "var(--font-display)",
                fontStyle: "italic",
                fontWeight: 400,
                fontSize: 26,
                lineHeight: 1.15,
                color: "var(--l2f-ink)",
              }}
            >
              {mode === "login" ? "Welcome back" : copy.title}
            </h3>
            <p className="text-sm text-muted-foreground text-center mt-1.5 max-w-[280px] mx-auto">
              {mode === "login"
                ? "Log in to continue."
                : copy.subtitle}
            </p>

            {mode === "signup" && (
              <ul className="mt-4 bg-secondary rounded-2xl p-4 space-y-2.5">
                {copy.benefits.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-2.5 text-[13px]"
                  >
                    <Check
                      size={16}
                      style={{ color: CORAL, marginTop: 1 }}
                      strokeWidth={3}
                    />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            )}

            <button
              type="button"
              onClick={handleGoogle}
              disabled={loading}
              className="mt-4 w-full py-3.5 rounded-full bg-white text-foreground relative flex items-center justify-center disabled:opacity-60"
              style={{
                border: "0.5px solid #D1D5DB",
                fontWeight: 500,
                fontSize: 14,
              }}
            >
              <svg
                className="absolute left-5"
                width="18"
                height="18"
                viewBox="0 0 48 48"
                aria-hidden="true"
              >
                <path
                  fill="#FFC107"
                  d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
                />
                <path
                  fill="#FF3D00"
                  d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
                />
                <path
                  fill="#4CAF50"
                  d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
                />
                <path
                  fill="#1976D2"
                  d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
                />
              </svg>
              Continue with Google
            </button>

            {!isAndroid() && (
              <button
                type="button"
                onClick={handleApple}
                disabled={loading}
                className="mt-3 w-full py-3.5 rounded-full bg-black text-white relative flex items-center justify-center disabled:opacity-60"
                style={{ fontWeight: 500, fontSize: 14 }}
              >
                <svg
                  className="absolute left-5"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M17.05 12.04c-.02-1.94 1.59-2.87 1.66-2.92-.91-1.33-2.32-1.51-2.82-1.53-1.2-.12-2.34.71-2.95.71-.61 0-1.55-.69-2.55-.67-1.31.02-2.52.76-3.2 1.94-1.36 2.36-.35 5.86 1 7.79.66.95 1.45 2.01 2.49 1.97 1-.04 1.38-.65 2.6-.65s1.56.65 2.62.63c1.08-.02 1.77-.96 2.43-1.92.76-1.1 1.08-2.17 1.1-2.22-.02-.01-2.11-.81-2.13-3.21zM15.13 6.07c.54-.66.91-1.57.81-2.49-.78.03-1.74.52-2.3 1.18-.5.58-.94 1.52-.82 2.41.88.07 1.77-.45 2.31-1.1z" />
                </svg>
                Continue with Apple
              </button>
            )}

            <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
              <div className="flex-1 h-px bg-border" />
              or
              <div className="flex-1 h-px bg-border" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email address"
                className="w-full px-4 py-3.5 rounded-2xl bg-secondary border border-transparent focus:outline-none focus:border-foreground text-sm"
              />
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={
                  mode === "signup" ? "Create a password" : "Password"
                }
                className="w-full px-4 py-3.5 rounded-2xl bg-secondary border border-transparent focus:outline-none focus:border-foreground text-sm"
              />
              {error && (
                <p className="text-sm" style={{ color: CORAL }}>
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-full font-medium text-white shadow-lg disabled:opacity-60"
                style={{ background: CORAL }}
              >
                {loading
                  ? "Please wait…"
                  : mode === "signup"
                    ? copy.cta
                    : "Log in →"}
              </button>
              {mode === "signup" && (
                <p className="text-[12px] text-center text-muted-foreground leading-snug pt-1">
                  By creating an account, you agree to our{" "}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                    style={{ color: CORAL }}
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline"
                    style={{ color: CORAL }}
                  >
                    Privacy Policy
                  </a>
                  .
                </p>
              )}
            </form>

            <p className="mt-4 text-[13px] text-center text-muted-foreground">
              {mode === "signup"
                ? "Already have an account? "
                : "New to Like2Fit? "}
              <button
                onClick={() => {
                  setMode(mode === "signup" ? "login" : "signup");
                  setError(null);
                }}
                className="font-medium"
                style={{ color: CORAL }}
              >
                {mode === "signup" ? "Log in" : "Back to sign up"}
              </button>
            </p>

            <button
              onClick={() => {
                trackEvent("signup_prompt_dismissed", {
                  trigger,
                  is_guest: true,
                  guest_session_id: getGuestSessionId(),
                });
                handleClose();
              }}
              className="mt-2 w-full py-2 text-sm text-muted-foreground"
            >
              Maybe later
            </button>
        </>


        <button
          onClick={handleClose}
          aria-label="Close"
          className="absolute top-3 right-3 p-2 text-muted-foreground"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
}
